import { getItemFromLocalStorage, getItemFromSessionStorage } from '@/helpers/localStorageServices';
import { bpSelection } from '@/services/auth/bpSelection';
import { session } from '@/services/auth/session';
import { tokenResolver } from '@/services/auth/tokenResolver';

// Public manager /auth/* endpoints that take NO token (pre-authentication or
// body-credential flows). Everything else under /auth/* is authenticated and gets
// the session (identity) token — sessions, mfa (status/enroll/disable), logout-all,
// switch-profile, change-password, set-password, providers connect/disconnect — as do
// non-/auth manager routes (/users/me, /business-profiles/*). Listing the public set
// (instead of the authenticated set) keeps new authenticated endpoints working by default.
const PUBLIC_AUTH_URLS = [
  '/auth/init',
  '/auth/access-token',
  '/auth/refresh-token',
  '/auth/refresh',
  '/auth/account-lookup',
  '/auth/register',
  '/auth/confirm',
  '/auth/resend-code',
  '/auth/login',
  '/auth/mfa/verify', // login-time MFA challenge (no token yet)
  '/auth/mfa/reset', // prefix: /mfa/reset/start, /mfa/reset/verify (locked-out, no token)
  '/auth/forgot-password',
  '/auth/reset-password',
  '/auth/oauth', // prefix: /oauth/{provider}/start, /oauth/callback, /oauth/exchange
];

function managerNeedsSessionToken(config) {
  const url = config.url || '';
  if (!url.startsWith('/auth/')) return true; // /business-profiles/*, other authed routes
  return !PUBLIC_AUTH_URLS.some((p) => url === p || url.startsWith(`${p}/`));
}

// Thin axios-facing facade. Token state + refresh logic live in services/auth/*; this only
// picks the right token per request and tracks consecutive auth failures. Manager auth requests
// use the session token; business-API requests use the per-BP app token (minted via switch-profile).
class TokenService {
  #refreshFailureCount = 0;
  static MAX_REFRESH_FAILURES = 5;

  // Single source of truth for resolving a request's internal businessProfileId. Priority:
  // explicit header → bpSelection intent → accountStore current selection → storage. The intent
  // is preferred because profile switching sets it eagerly BEFORE the store updates, so in-flight
  // requests already target the new BP. It is VETOED only when KNOWN foreign (the owned set is
  // loaded and the id isn't in it). NEVER falls back to the Amazon profileId.
  async resolveBusinessProfileId(config) {
    let id = config.headers?.businessProfileId;

    const { useAccountStore } = await import('@/stores/account.js');
    const accountStore = useAccountStore();

    if (!id && typeof window !== 'undefined') {
      try {
        const path = window.location.pathname;
        const isAuthFlow =
          path.includes('/register') || path.includes('/create-business') || path.includes('/login');
        if (!isAuthFlow) {
          const intentId = bpSelection.get();
          if (intentId) {
            const owned = accountStore.businessProfiles;
            const isKnownForeign = owned?.length && !owned.some((p) => p.id === +intentId);
            if (!isKnownForeign) id = intentId;
          }
        }
      } catch {
        /* ignore */
      }
    }

    if (!id) id = accountStore.currentBusinessProfile?.id;

    if (!id) {
      id =
        getItemFromSessionStorage('currentBusinessProfile')?.id ??
        getItemFromLocalStorage('currentBusinessProfile')?.id;
    }

    return id ? String(id) : null;
  }

  // Default-vs-per-BP routing, refresh-or-mint and the ERR_NO_SESSION contract all live in
  // tokenResolver (the single owner of the "default profile is served by the session token"
  // rule) — this facade only picks the token per request and tracks consecutive failures.
  async getValidAppToken(profileId) {
    return tokenResolver.getValidTokenFor(profileId);
  }

  // Single entry point from the axios interceptor — picks the right token for each request.
  async getTokenForRequest(config) {
    if (config.__client === 'manager') {
      // requestUrl lets getSessionToken avoid the per-BP refresh branch for /auth/switch-profile,
      // whose caller already holds that BP's refresh lock (non-reentrant — see session.js).
      // skipRefresh mirrors __noSessionRefresh: the caller holds the SESSION lock, so any refresh
      // attempt for this request would deadlock (see backend-auth.service.switchProfile).
      return managerNeedsSessionToken(config)
        ? session.getSessionToken({ requestUrl: config.url, skipRefresh: !!config.__noSessionRefresh })
        : null;
    }
    const profileId = await this.resolveBusinessProfileId(config);
    if (!profileId) return null;
    return this.getValidAppToken(profileId);
  }

  // Force-refresh after a 401: manager requests rotate the session token; business requests
  // force-rotate via the resolver (default profile → session rotation; non-default → a per-BP
  // remint that bypasses the local-expiry cache but does NOT record last-used).
  async invalidateAndRefresh(config, profileId) {
    if (config.__client === 'manager') {
      // /auth/switch-profile always carries the identity (session) token, so a newer one already in
      // shared storage means the session was rotated after this request was built — adopt it
      // instead of rotating again. Other manager routes may carry a per-BP token: always rotate.
      const rejectedToken =
        config.url === '/auth/switch-profile'
          ? String(config.headers?.Authorization ?? '').replace(/^Bearer /, '') || null
          : null;
      await session.refreshSession(true, { rejectedToken });
      return;
    }
    if (profileId) await tokenResolver.forceRefresh(profileId);
  }

  // Called after a 401 that survived a retry. One shared counter, reset on any success, logout
  // at the threshold.
  async handleRefreshFailure() {
    this.#refreshFailureCount += 1;
    console.error(
      `[auth] request kept failing with 401 after a token refresh (${this.#refreshFailureCount}/${TokenService.MAX_REFRESH_FAILURES}) — backend rejects freshly issued tokens.`,
    );
    if (this.#refreshFailureCount >= TokenService.MAX_REFRESH_FAILURES) {
      this.#refreshFailureCount = 0;
      console.error('[auth] max consecutive auth failures reached — forcing logout.');
      await session.logout({ source: 'forced' });
      const error = new Error('Maximum consecutive auth failures. Logging out.');
      error.code = 'ERR_AUTH_LOGOUT';
      throw error;
    }
  }

  resetFailureCount() {
    this.#refreshFailureCount = 0;
  }
}

export const tokenService = new TokenService();
